/* CKC — stand-alone shader starfield for the theme's star sections.
   Same shader as the home-page hero (hero-starfield.js), ported to raw WebGL
   so it costs ~5 KB instead of loading three.js. One context per star
   section (capped at 8), rendered only while that section is on screen, and
   only the visible slice of a tall section is ever drawn. */
(function () {
  'use strict';
  if (document.querySelector('[data-hero-3d]')) return;                 // home page: hero owns the stars
  if (matchMedia('(prefers-reduced-motion: reduce)').matches) return;
  /* Perf (24 Sept): skip star sections on a LIGHT background (e.g. the Lunar
     Gray stat cards). White stars are invisible there, so a WebGL pass per
     frame was pure cost. Luminance is read from the section's own computed
     background colour. */
  function isLight(el) {
    var m = (getComputedStyle(el).backgroundColor || '').match(/[\d.]+/g);
    if (!m || m.length < 3 || (m.length > 3 && +m[3] === 0)) return false;
    return (0.2126 * m[0] + 0.7152 * m[1] + 0.0722 * m[2]) / 255 > 0.55;
  }
  var targets = Array.prototype.slice.call(document.querySelectorAll('.mask-overlay-stars'))
    .filter(function (el) { return !isLight(el); }).slice(0, 8);
  if (!targets.length) return;

  var phone = innerWidth < 750;
  /* Perf (24 Sept): 1x pixel ratio everywhere (soft stars look the same, ~55%
     fewer pixels on retina) and ~30 fps while the page is still — the twinkle
     is slow, so drawing every idle frame only competed with the parallax. */
  var DPR = 1, FRAME_MS = 33;
  var INTENSITY = 0.62, HAZE = 0.08;

  var VERT = 'attribute vec2 p;varying vec2 vUv;void main(){vUv=p*0.5+0.5;gl_Position=vec4(p,0.0,1.0);}';
  /* layer(): one small star per cell, glow ~0 by the cell edge.
     haloLayer(): the rare wide-halo stars on a sparse grid (4x the cell area),
     pinned near the cell centre and feathered to zero inside the cell, so the
     glow is never cut off in a square. */
  var FRAG = [
    'precision highp float;',
    'uniform float uTime,uScroll,uDrift,uIntensity,uHaze,uAspect,uQuality;',
    'uniform vec2 uPointer;uniform float uTop,uUnit,uH;varying vec2 vUv;',
    'float hash21(vec2 p){p=fract(p*vec2(123.34,456.21));p+=dot(p,p+45.32);return fract(p.x*p.y);}',
    'vec3 hash23(vec2 p){float h=hash21(p);return vec3(h,hash21(p+h+1.7),hash21(p+h+9.1));}',
    'float vnoise(vec2 p){vec2 i=floor(p),f=fract(p);f=f*f*(3.0-2.0*f);return mix(mix(hash21(i),hash21(i+vec2(1,0)),f.x),mix(hash21(i+vec2(0,1)),hash21(i+vec2(1,1)),f.x),f.y);}',
    'vec4 layer(vec2 uv,float cells,float seed,float twk,float keep){',
    '  vec2 g=uv*cells;vec2 id=floor(g);vec2 f=fract(g);vec3 h=hash23(id+seed);',
    '  float pres=step(1.0-keep,h.z);vec2 sp=0.2+0.6*h.xy;float d=length(f-sp);',
    '  float sz=0.013+0.034*h.z*h.z;float core=smoothstep(sz,sz*0.25,d);',
    '  float glow=exp(-d*d/(sz*sz*4.5))*0.2;',
    '  float tw=0.70+0.30*sin(uTime*(0.5+h.z*1.8)+h.x*6.2832);',
    '  float b=pres*(core+glow)*mix(1.0,tw,twk);',
    '  vec3 tint=mix(vec3(0.78,0.86,1.0),vec3(1.0,0.92,0.76),step(0.72,h.x));',
    '  return vec4(tint,b);}',
    'vec4 haloLayer(vec2 uv,float cells,float seed,float twk,float keep){',
    '  vec2 g=uv*cells;vec2 id=floor(g);vec2 f=fract(g);vec3 h=hash23(id+seed);',
    '  float pres=step(1.0-keep,hash21(id+seed+3.3));vec2 sp=0.36+0.28*h.xy;float d=length(f-sp);',
    '  float sz=0.014+0.0095*h.z;float core=smoothstep(sz,sz*0.25,d);',
    '  float glow=exp(-d*d/(sz*sz*4.5))*0.2;',
    '  float halo=exp(-d*d/(sz*sz*40.0))*0.22*smoothstep(0.36,0.22,d);',
    '  float tw=0.70+0.30*sin(uTime*(0.5+h.z*1.8)+h.x*6.2832);',
    '  float b=pres*((core+glow)*mix(1.0,tw,twk)+halo);',
    '  vec3 tint=mix(vec3(0.78,0.86,1.0),vec3(1.0,0.92,0.76),step(0.72,h.x));',
    '  return vec4(tint,b);}',
    'void main(){',
    '  vec2 uv=vec2(gl_FragCoord.x,-(uTop+uH-gl_FragCoord.y))/uUnit;vec2 par=uPointer*0.03;',
    '  vec2 base=vec2(-uDrift*0.18,uScroll*0.55);vec3 col=vec3(0.0);float a=0.0;',
    '  vec4 s1=layer(uv+base*0.20+par*0.25,24.0,1.0,0.5,0.42);col+=s1.rgb*s1.a*0.42;a+=s1.a*0.42;',
    '  vec4 b1=haloLayer(uv+base*0.20+par*0.25,12.0,1.0,0.5,0.042);col+=b1.rgb*b1.a*0.42;a+=b1.a*0.42;',
    '  vec4 s2=layer(uv+base*0.45+par*0.6,12.0,7.0,0.8,0.40);col+=s2.rgb*s2.a*0.75;a+=s2.a*0.75;',
    '  vec4 b2=haloLayer(uv+base*0.45+par*0.6,6.0,7.0,0.8,0.04);col+=b2.rgb*b2.a*0.75;a+=b2.a*0.75;',
    '  if(uQuality>0.5){vec4 s3=layer(uv+base*0.85+par*1.1,6.0,13.0,1.0,0.33);col+=s3.rgb*s3.a;a+=s3.a;',
    '    vec4 b3=haloLayer(uv+base*0.85+par*1.1,3.0,13.0,1.0,0.033);col+=b3.rgb*b3.a;a+=b3.a;}',
    '  col*=uIntensity;a*=uIntensity;',
    '  float n=vnoise(uv*1.5+base*0.12+vec2(uTime*0.012,0.0));n=n*0.62+0.38*vnoise(uv*3.4-base*0.08+11.0);',
    '  float haze=smoothstep(0.48,0.98,n)*uHaze;col+=vec3(0.16,0.34,0.70)*haze;a+=haze;',
    '  gl_FragColor=vec4(col,clamp(a,0.0,1.0));}'
  ].join('\n');

  function build(el) {
    var c = document.createElement('canvas');
    c.className = 'ckc-stars__canvas';
    var gl = c.getContext('webgl', { alpha: true, premultipliedAlpha: true, antialias: false, depth: false, stencil: false, powerPreference: 'low-power' });
    if (!gl) return null;
    function sh(type, src) { var s = gl.createShader(type); gl.shaderSource(s, src); gl.compileShader(s); return gl.getShaderParameter(s, gl.COMPILE_STATUS) ? s : null; }
    var vs = sh(gl.VERTEX_SHADER, VERT), fs = sh(gl.FRAGMENT_SHADER, FRAG);
    if (!vs || !fs) return null;
    var prog = gl.createProgram(); gl.attachShader(prog, vs); gl.attachShader(prog, fs); gl.linkProgram(prog);
    if (!gl.getProgramParameter(prog, gl.LINK_STATUS)) return null;
    gl.useProgram(prog);
    var buf = gl.createBuffer(); gl.bindBuffer(gl.ARRAY_BUFFER, buf);
    gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([-1, -1, 3, -1, -1, 3]), gl.STATIC_DRAW);
    var loc = gl.getAttribLocation(prog, 'p'); gl.enableVertexAttribArray(loc); gl.vertexAttribPointer(loc, 2, gl.FLOAT, false, 0, 0);
    var u = {};
    ['uTime', 'uScroll', 'uDrift', 'uIntensity', 'uHaze', 'uAspect', 'uQuality', 'uPointer', 'uTop', 'uUnit', 'uH'].forEach(function (n) { u[n] = gl.getUniformLocation(prog, n); });
    gl.uniform1f(u.uQuality, phone ? 0 : 1);
    gl.uniform1f(u.uDrift, 0);
    var iv = parseFloat(el.getAttribute('data-stars')), hz = parseFloat(el.getAttribute('data-stars-haze'));
    gl.uniform1f(u.uIntensity, isFinite(iv) ? iv : INTENSITY);
    gl.uniform1f(u.uHaze, isFinite(hz) ? hz : HAZE);
    gl.clearColor(0, 0, 0, 0);
    el.insertBefore(c, el.firstChild);
    el.classList.add('ckc-stars--live');
    return { el: el, c: c, gl: gl, u: u, visible: false, w: 0, h: 0 };
  }

  var items = [];
  for (var i = 0; i < targets.length; i++) { var it = build(targets[i]); if (it) items.push(it); }
  if (!items.length) return;
  document.documentElement.classList.add('ckc-stars');

  var running = false, last = 0, ptx = 0, pty = 0, ptxT = 0, ptyT = 0;
  function anyVisible() { for (var k = 0; k < items.length; k++) if (items[k].visible) return true; return false; }
  function kick() { if (!running && !document.hidden && anyVisible()) { running = true; last = 0; requestAnimationFrame(frame); } }

  if ('IntersectionObserver' in window) {
    var io = new IntersectionObserver(function (es) {
      for (var k = 0; k < es.length; k++) for (var j = 0; j < items.length; j++) if (items[j].el === es[k].target) items[j].visible = es[k].isIntersecting;
      kick();
    }, { rootMargin: '25% 0px' });
    items.forEach(function (it) { io.observe(it.el); });
  } else { items.forEach(function (it) { it.visible = true; }); }
  document.addEventListener('visibilitychange', kick);
  if (!phone) addEventListener('pointermove', function (e) { ptxT = e.clientX / innerWidth - 0.5; ptyT = 0.5 - e.clientY / innerHeight; }, { passive: true });

  function frame(now) {
    if (document.hidden || !anyVisible()) { running = false; return; }
    /* idle: twinkle at ~30 fps; while the page is scrolling, every frame, so the
       canvas slice keeps pace with the section and never lags behind */
    var sy0 = window.scrollY || 0;
    if (last && sy0 === frame._sy && now - last < FRAME_MS) { requestAnimationFrame(frame); return; }
    frame._sy = sy0;
    var dt = Math.min(0.1, (now - (last || now)) / 1000); last = now;
    var k = 1 - Math.exp(-3 * dt); ptx += (ptxT - ptx) * k; pty += (ptyT - pty) * k;
    var vh = innerHeight, sy = window.scrollY || 0;
    var docMax = Math.max(1, document.documentElement.scrollHeight - vh);
    var scroll = sy / docMax;
    for (var j = 0; j < items.length; j++) {
      var it = items[j];
      if (!it.visible) continue;
      var r = it.el.getBoundingClientRect();
      var secH = r.height, w = r.width;
      if (secH < 2 || w < 2) continue;
      /* draw only a viewport-sized slice, and slide it to wherever the
         section is currently showing */
      var sliceH = Math.min(secH, vh + 160);
      var off = Math.max(0, Math.min(secH - sliceH, -r.top - 80));
      it.c.style.transform = 'translateY(' + off.toFixed(1) + 'px)';
      var pw = Math.round(w * DPR), ph = Math.round(sliceH * DPR);
      if (it.w !== pw || it.h !== ph) {
        it.c.width = pw; it.c.height = ph; it.w = pw; it.h = ph;
        it.c.style.height = sliceH + 'px';
        it.gl.viewport(0, 0, pw, ph);
      }
      var gl = it.gl;
      gl.uniform1f(it.u.uTime, now / 1000);
      gl.uniform1f(it.u.uScroll, scroll);
      gl.uniform1f(it.u.uAspect, pw / ph);
      /* CKC 4 Oct: every star section samples ONE screen-wide field (screen
         coordinates, one shared scale), so where two star sections meet the
         stars and haze carry straight across - no seam. Same cost as before. */
      gl.uniform1f(it.u.uTop, (r.top + off) * DPR);
      gl.uniform1f(it.u.uUnit, (vh + 160) * DPR);
      gl.uniform1f(it.u.uH, ph);
      gl.uniform2f(it.u.uPointer, ptx, pty);
      gl.clear(gl.COLOR_BUFFER_BIT);
      gl.drawArrays(gl.TRIANGLES, 0, 3);
    }
    requestAnimationFrame(frame);
  }
  kick();
})();
